/**
 * Mic capture pipeline: getUserMedia → AudioWorklet → {@link Utterer}.
 *
 * This file owns only the device plumbing. The VAD-gated utterance
 * segmentation (and its ordering-sensitive frame serialization) lives in
 * {@link Utterer} in ./segmenter.ts so it can be tested without the DOM.
 *
 * Emits a stream of {@link MicEvent}s:
 *   - {type:"start"}            — silence broke; new utterance begins
 *   - {type:"pcm", data:Int16}  — voiced samples (every 32 ms frame)
 *   - {type:"end"}              — trailing silence long enough; utterance closed
 *   - {type:"level", rms01}     — cheap meter for the UI (every frame)
 */

import { FRAME_SAMPLES, SAMPLE_RATE, type Vad } from "./vad";
import { Utterer, type MicEvent } from "./segmenter";

// Back-compat re-exports: existing imports (engine, LiveScreen, EnrollScreen)
// still reach these through "../audio/mic".
export type { MicEvent } from "./segmenter";
export { floatToPcm16, pcm16ToWavBlob } from "./pcm";

export class MicCapture {
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private node: AudioWorkletNode | null = null;
  private readonly seg: Utterer;

  constructor(private readonly vad: Vad, onEvent: (e: MicEvent) => void) {
    this.seg = new Utterer(vad, onEvent);
  }

  async start(): Promise<void> {
    await this.vad.init();
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      video: false,
    });
    // Request 16 kHz sample rate from the AudioContext — Chrome/Firefox/Edge will
    // perform high-quality anti-aliased resampling internally. If the browser
    // rejects 16 kHz (some Safari versions), fall back to native rate and let
    // the worklet resample.
    try {
      this.ctx = new AudioContext({ sampleRate: SAMPLE_RATE, latencyHint: "interactive" });
    } catch (err) {
      console.warn("Target 16kHz AudioContext rejected by browser, falling back to native sample rate:", err);
      this.ctx = new AudioContext({ latencyHint: "interactive" });
    }
    await this.ctx.audioWorklet.addModule("/app/mic-worklet.js");
    const source = this.ctx.createMediaStreamSource(this.stream);
    this.node = new AudioWorkletNode(this.ctx, "mic-worklet");
    this.node.port.onmessage = (ev) => {
      const frame = ev.data as Float32Array;
      if (frame.length === FRAME_SAMPLES) this.seg.push(frame);
    };
    source.connect(this.node);
    // The node has no downstream sink; we don't `connect()` it to destination.
  }

  async stop(): Promise<void> {
    try { this.node?.disconnect(); } catch { /* ignore */ }
    this.node = null;
    try { await this.ctx?.close(); } catch { /* ignore */ }
    this.ctx = null;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.seg.stop();
  }

  /**
   * Re-arm segmentation without tearing down the audio graph. Used after
   * half-duplex TTS playback so any utterance the segmenter half-captured while
   * muted is discarded and the next real speech onset opens a fresh utterance.
   */
  reset(): void {
    this.seg.reset();
  }
}
